import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  confirmMerchantPortalForgotPassword,
  getMerchantToken,
  requestMerchantPortalForgotPassword,
} from "../services/api";
import { validateMerchantPortalPassword } from "../utils/merchantPortalPassword";
import { AuthShell } from "../components/AuthShell";
import { friendlyError } from "../utils/friendlyError";

import { t } from "../i18n";
const labelStyle = { display: "block", marginBottom: 4, fontSize: 13, color: "#1b1530", fontWeight: 600 };
const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  backgroundColor: "#ffffff",
  border: "1px solid #e6e1f0",
  color: "#1b1530",
  borderRadius: 12,
};

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = `${t("forgot.doc_title")} · ${t("title.suffix")}`;
  }, []);

  if (getMerchantToken()) {
    return <Navigate to="/" replace />;
  }

  async function sendCode(e) {
    e.preventDefault();
    setError("");
    const em = email.trim();
    if (!em) {
      setError(t("login.email_required"));
      return;
    }
    setBusy(true);
    try {
      await requestMerchantPortalForgotPassword(em);
      setStep(2);
    } catch (err) {
      setError(friendlyError(err, t("forgot.request_failed")));
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(e) {
    e.preventDefault();
    setError("");
    const em = email.trim();
    const code = otp.trim();
    const pwErr = validateMerchantPortalPassword(newPassword);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    if (newPassword !== newPassword2) {
      setError(t("forgot.mismatch"));
      return;
    }
    if (!code) {
      setError(t("forgot.code_required"));
      return;
    }
    setBusy(true);
    try {
      await confirmMerchantPortalForgotPassword(em, code, newPassword);
      setStep(3);
    } catch (err) {
      setError(friendlyError(err, t("forgot.reset_failed")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell eyebrow={t("forgot.eyebrow")} title={t("forgot.title")}>
        <p className="auth-card__lead">
          {step === 1 ? t("forgot.lead_1") : step === 2 ? t("forgot.lead_2") : t("forgot.lead_3")}
        </p>

        {step === 1 ? (
          <form onSubmit={sendCode}>
            <label style={labelStyle}>
              {t("login.email_label")}
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder={t("login.email_placeholder")}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            {error ? (
              <div
                style={{
                  marginTop: 16,
                  padding: "10px 12px",
                  backgroundColor: "#fbefec",
                  color: "#a63a3a",
                  borderRadius: 12,
                  border: "1px solid #e3bdb6",
                  fontSize: 14,
                }}
              >
                {error}
              </div>
            ) : null}
            <button
              type="submit"
              className="primary"
              disabled={busy}
              style={{
                marginTop: 20,
                width: "100%",
                padding: "12px 16px",
                color: "#ffffff",
                border: "none",
                borderRadius: 12,
                cursor: busy ? "not-allowed" : "pointer",
                fontSize: 15,
                fontWeight: 500,
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? t("forgot.sending") : t("forgot.send_code")}
            </button>
          </form>
        ) : null}

        {step === 2 ? (
          <form onSubmit={resetPassword}>
            <label style={labelStyle}>
              {t("login.email_label")}
              <input
                type="email"
                autoComplete="email"
                readOnly
                value={email}
                style={{ ...inputStyle, marginTop: 6, opacity: 0.85 }}
              />
            </label>
            <label style={{ ...labelStyle, marginTop: 16 }}>
              {t("forgot.code_label")}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(ev) => setOtp(ev.target.value)}
                placeholder={t("forgot.code_placeholder")}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            <label style={{ ...labelStyle, marginTop: 16 }}>
              {t("forgot.new_password")}
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(ev) => setNewPassword(ev.target.value)}
                placeholder={t("forgot.password_hint")}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            <label style={{ ...labelStyle, marginTop: 16 }}>
              {t("forgot.confirm_password")}
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword2}
                onChange={(ev) => setNewPassword2(ev.target.value)}
                style={{ ...inputStyle, marginTop: 6 }}
              />
            </label>
            {error ? (
              <div
                style={{
                  marginTop: 16,
                  padding: "10px 12px",
                  backgroundColor: "#fbefec",
                  color: "#a63a3a",
                  borderRadius: 12,
                  border: "1px solid #e3bdb6",
                  fontSize: 14,
                }}
              >
                {error}
              </div>
            ) : null}
            <button
              type="submit"
              className="primary"
              disabled={busy}
              style={{
                marginTop: 20,
                width: "100%",
                padding: "12px 16px",
                color: "#ffffff",
                border: "none",
                borderRadius: 12,
                cursor: busy ? "not-allowed" : "pointer",
                fontSize: 15,
                fontWeight: 500,
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? t("forgot.updating") : t("forgot.update")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setStep(1);
                setOtp("");
                setNewPassword("");
                setNewPassword2("");
                setError("");
              }}
              style={{
                marginTop: 12,
                width: "100%",
                padding: "10px 16px",
                color: "#6a6480",
                border: "1px solid #e6e1f0",
                borderRadius: 12,
                background: "transparent",
                cursor: busy ? "not-allowed" : "pointer",
                fontSize: 14,
              }}
            >
              {t("forgot.different_email")}
            </button>
          </form>
        ) : null}

        {step === 3 ? (
          <Link
            to="/login"
            className="primary"
            style={{
              display: "block",
              marginTop: 8,
              textAlign: "center",
              padding: "12px 16px",
              borderRadius: 12,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            {t("forgot.back_to_sign_in")}
          </Link>
        ) : null}

        <p style={{ marginTop: 24, fontSize: 14, color: "#6a6480", textAlign: "center" }}>
          <Link to="/login" style={{ color: "#6b4eaa", fontWeight: 600 }}>
            {t("common.cancel")}
          </Link>
        </p>
    </AuthShell>
  );
}
